import languageIgnorePatternsJson from '../data/language_ignore_patterns.json';

/**
 * Curated open/close pairs for preprocessMultilinePatterns (wave 1).
 * JSON "multiline" strings are mostly examples; only safe delimiter pairs are enabled here.
 */
const MULTILINE_PAIRS_BY_SLUG = {
  python: [{ openPattern: '#region', closePattern: '#endregion' }],
  csharp: [{ openPattern: '#region', closePattern: '#endregion' }],
  ruby: [{ openPattern: '=begin', closePattern: '=end' }],
  kotlin: [{ openPattern: '// region', closePattern: '// endregion' }]
};

const basePattern = (type, extra = {}) => ({
  type,
  caseSensitive: false,
  enabled: true,
  ...extra
});

function patternsFromSlugEntry(slug, entry) {
  const { patterns } = entry;
  if (!patterns) return [];

  const out = [];

  (patterns.simple || []).forEach(pattern => {
    out.push(basePattern('simple', { pattern }));
  });

  (patterns.comment || []).forEach(pattern => {
    out.push(basePattern('comment', { pattern }));
  });

  (MULTILINE_PAIRS_BY_SLUG[slug] || []).forEach(({ openPattern, closePattern }) => {
    out.push(basePattern('multiline', { openPattern, closePattern }));
  });

  return out;
}

function buildBuiltinMap() {
  const map = Object.create(null);

  for (const slug of Object.keys(languageIgnorePatternsJson)) {
    const entry = languageIgnorePatternsJson[slug];
    const list = patternsFromSlugEntry(slug, entry);
    const label = entry.language;
    if (label) {
      map[label] = list;
    }
    if (slug === 'c') {
      map['C++'] = list;
      map.C = list;
    }
  }

  return map;
}

const BUILTIN_BY_LANGUAGE = buildBuiltinMap();

const FALLBACK_SIMPLE = ['TODO', 'FIXME', 'DEBUG', 'REMEMBER'];

/**
 * Wave 1 built-in ignore patterns for supported languages (simple + comment + curated multiline).
 * @param {string} language - Display name from page metadata (e.g. "Python", "C++")
 * @returns {Array<object>} Pattern objects compatible with Settings / codeProcessing
 */
export function getBuiltinIgnorePatternsForLanguage(language) {
  if (!language) return [];
  const list = BUILTIN_BY_LANGUAGE[language];
  if (list && list.length) {
    return list.map(p => ({
      ...p,
      caseSensitive: p.caseSensitive ?? false,
      enabled: p.enabled !== false
    }));
  }
  return FALLBACK_SIMPLE.map(pattern =>
    basePattern('simple', { pattern })
  );
}

/**
 * Short list of example strings for the settings UI (simple + comment only).
 * @param {string} language
 * @returns {string[]}
 */
export function getBuiltinSuggestionStringsForLanguage(language) {
  return getBuiltinIgnorePatternsForLanguage(language)
    .filter(p => p.type === 'simple' || p.type === 'comment')
    .map(p => p.pattern);
}

export { BUILTIN_BY_LANGUAGE, FALLBACK_SIMPLE };
